import { useNavigate } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Button, ButtonLink } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormLabel } from "design-system/organisms/Form"
import { ensure } from "lopermut"
import { ArrowUpRight, Plus, SearchIcon, Trash2 } from "lucide-react"
import { WhatsappTemplatePreview } from "modules/campaigns/components/Sections/channels/whatsapp/WhatsappTemplatePreview"
import { useWhatsappTemplatesQuery } from "modules/campaigns/hooks/sections/whatsapp/useWhatsappTemplatesQuery"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import { useWhatsappNumbersQuery } from "modules/settings/hooks/whatsapp/useWhatsappNumbersQuery"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { useLanguageLabel, useLanguageOptions } from "modules/shared/hooks/useLanguageLabel"
import { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Route as ConfigurationRoute } from "routes/_base/_auth/campaigns/$id/configuration/route"
import { CampaignStatusEnum, WhatsappTemplateResponseDataSchema } from "schema"
import { z } from "zod"
import { CampaignTemplateStatus } from "./CampaignTemplateStatus"

type Props = { campaignId: string; isDisabled: boolean }
type FormTemplate = z.infer<typeof UpdateCampaignSchemaFrontend>["templates"][number]
type AvailableTemplate = z.infer<typeof WhatsappTemplateResponseDataSchema>
const ITEMS_PER_PAGE = 10
const templateSorts = {
  recentlyCreated: { field: "id", order: "desc" },
  recentlyUsed: { field: "lastUsedAt", order: "desc" },
  recentlyUpdated: { field: "updatedAt", order: "desc" },
  alphabetical: { field: "name", order: "asc" },
} as const

function sameLanguage(first: string, second: string) {
  return first.split(/[-_]/)[0]?.toLowerCase() === second.split(/[-_]/)[0]?.toLowerCase()
}

export function WhatsappTemplatesPicker({ campaignId, isDisabled }: Props) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const navigate = useNavigate()
  const { templatePicker } = ConfigurationRoute.useSearch()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const { data: campaign } = useCampaignQuery(campaignId)
  const isActive = campaign?.status === CampaignStatusEnum.Active
  const addUnavailableReason = t(
    isActive
      ? "campaigns.templates.whatsapp.campaign.pauseToEdit"
      : "campaigns.templates.whatsapp.campaign.addUnavailable",
  )
  const { data: numbers } = useWhatsappNumbersQuery()
  const templates = form.watch("templates") ?? []
  const defaultLanguage = form.watch("defaultLanguage")
  const whatsappNumberId = form.watch("whatsappNumberId") ?? undefined

  useEffect(() => {
    if (!isDisabled && numbers?.length && !whatsappNumberId) {
      form.setValue("whatsappNumberId", ensure(numbers.at(0)).id, { shouldDirty: true })
    }
  }, [numbers, whatsappNumberId, form, isDisabled])

  const groups = templates.reduce((map, template) => {
    const items = map.get(template.language) ?? []
    items.push(template)
    map.set(template.language, items)
    return map
  }, new Map<string, FormTemplate[]>())
  const sortedGroups = [...groups].sort(([a], [b]) => {
    const aIsDefault = Boolean(defaultLanguage && sameLanguage(a, defaultLanguage))
    const bIsDefault = Boolean(defaultLanguage && sameLanguage(b, defaultLanguage))
    if (aIsDefault !== bIsDefault) return aIsDefault ? -1 : 1
    return languageLabel(a).localeCompare(languageLabel(b))
  })
  const persistedById = new Map(
    campaign?.templates
      ?.filter((template) => template.channel === "Whatsapp")
      .map((template) => [template.id, template]) ?? [],
  )
  function setPicker(language?: string) {
    navigate({
      to: "/campaigns/$id/configuration",
      params: { id: campaignId },
      search: { section: "templates", templatePicker: language },
    })
  }

  function removeTemplate(id: string) {
    form.setValue(
      "templates",
      templates.filter((template) => template.id !== id),
      {
        shouldDirty: true,
      },
    )
  }

  function addTemplates(selected: AvailableTemplate[]) {
    const additions = selected
      .filter((template) => template.status !== "REJECTED")
      .map((template) => ({
        id: template.id,
        name: template.name,
        status: template.status,
        language: template.language,
        header: template.header,
        body: template.body,
        footer: template.footer,
        mediaUrl: template.mediaUrl,
        mediaFormat: template.mediaFormat,
        whatsappTemplateButtons: template.whatsappTemplateButtons,
        whatsappTemplateVariables: template.whatsappTemplateVariables,
      }))
    form.setValue("templates", [...templates, ...additions], { shouldDirty: true })
    setPicker()
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2">
        <FormLabel>{t("campaigns.channel.whatsapp.number")}</FormLabel>
        <p className="text-sm text-muted-foreground">
          {t("campaigns.channel.whatsapp.numberDescription")}
        </p>
        <Select
          value={whatsappNumberId}
          onValueChange={(value) => form.setValue("whatsappNumberId", value, { shouldDirty: true })}
          disabled={isDisabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("campaigns.channel.whatsapp.number")} />
          </SelectTrigger>
          <SelectContent>
            {numbers?.map((number) => (
              <SelectItem key={number.id} value={number.id}>
                {number.number}
                {" - "}
                {number.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <FormLabel>{t("campaigns.channel.whatsapp.template")}</FormLabel>
          <p className="text-sm text-muted-foreground">
            {t("campaigns.channel.templatesSubtitle")}
          </p>
        </div>
        {templates.length > 0 && (
          <Tooltip open={isDisabled ? undefined : false}>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={isDisabled ? 0 : undefined}>
                <Button type="button" disabled={isDisabled} onClick={() => setPicker("all")}>
                  <Plus className="size-4" />
                  {t("campaigns.templates.whatsapp.campaign.add.title")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{addUnavailableReason}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {templates.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t("campaigns.templates.whatsapp.insights.empty")}
          </p>
          <Tooltip open={isDisabled ? undefined : false}>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={isDisabled ? 0 : undefined}>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isDisabled}
                  onClick={() => setPicker("all")}
                >
                  <Plus className="size-4" />
                  {t("campaigns.templates.whatsapp.campaign.add.title")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{addUnavailableReason}</TooltipContent>
          </Tooltip>
        </div>
      )}
      {sortedGroups.map(([language, items]) => (
        <section key={language} className="flex flex-col gap-3">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <h4 className="font-medium">
              {languageLabel(language)}
              {defaultLanguage &&
                sameLanguage(language, defaultLanguage) &&
                ` · ${t("campaigns.templates.whatsapp.campaign.defaultLanguage")}`}
            </h4>
            {!isDisabled && campaign?.status === CampaignStatusEnum.Draft && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setPicker(language)}>
                <Plus className="size-4" />
                {t("campaigns.templates.whatsapp.campaign.addTemplate")}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {items.map((template) => {
              const persisted = persistedById.get(template.id)
              return (
                <div
                  key={template.id}
                  className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{template.name}</span>
                        {template.status && template.status !== "APPROVED" && (
                          <Badge variant={template.status === "REJECTED" ? "negative" : "warning"}>
                            {t(`campaigns.templates.whatsapp.status.${template.status}`)}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {languageLabel(template.language)}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {persisted ? (
                        <CampaignTemplateStatus
                          campaignId={campaignId}
                          templateId={template.id}
                          status={persisted.active ? "active" : "inactive"}
                          disabled={isDisabled}
                        />
                      ) : (
                        <Badge variant="success">
                          {t("campaigns.templates.whatsapp.actions.active")}
                        </Badge>
                      )}
                      {!isDisabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          aria-label={t("campaigns.templates.whatsapp.campaign.remove")}
                          title={t("campaigns.templates.whatsapp.campaign.remove")}
                          onClick={() => removeTemplate(template.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <TemplatePreview template={template} />
                </div>
              )
            })}
          </div>
        </section>
      ))}

      <TemplatePickerDialog
        open={templatePicker !== undefined && !isDisabled}
        language={templatePicker === "all" ? undefined : templatePicker}
        selectedIds={templates.map((template) => template.id)}
        onClose={() => setPicker()}
        onAdd={addTemplates}
      />
    </div>
  )
}

function TemplatePreview({ template }: { template: FormTemplate }) {
  if (!template.body) return null
  return (
    <WhatsappTemplatePreview
      templateId={template.id}
      header={template.header ?? undefined}
      body={template.body}
      footer={template.footer ?? undefined}
      media={{
        format: template.mediaFormat as AvailableTemplate["mediaFormat"],
        url: template.mediaUrl,
      }}
      variables={template.whatsappTemplateVariables}
      buttonTexts={template.whatsappTemplateButtons?.map((button) => button.text)}
      className="rounded-md"
    />
  )
}

type PickerProps = {
  open: boolean
  language?: string
  selectedIds: string[]
  onClose: () => void
  onAdd: (templates: AvailableTemplate[]) => void
}

function TemplatePickerDialog({ open, language, selectedIds, onClose, onAdd }: PickerProps) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const languageOptions = useLanguageOptions()
  const [query, setQuery] = useState("")
  const [selectedLanguage, setSelectedLanguage] = useState(language ?? "all")
  const [selectedOrder, setSelectedOrder] = useState<keyof typeof templateSorts>("recentlyCreated")
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [chosen, setChosen] = useState<Map<string, AvailableTemplate>>(new Map())
  const {
    data: templates,
    isFetching,
    isPending,
  } = useWhatsappTemplatesQuery({
    query: {
      query,
      page,
      total: ITEMS_PER_PAGE,
      filterBy: selectedLanguage === "all" ? undefined : { language: selectedLanguage },
      sortBy: templateSorts[selectedOrder],
      excludeIds: selectedIds.join(","),
    },
    enabled: open,
    onSuccess: (data) => setCount(data.pageInfo.count),
  })
  const isLoadingTemplates = isPending || isFetching

  useEffect(() => {
    if (open) {
      setQuery("")
      setSelectedLanguage(language ?? "all")
      setSelectedOrder("recentlyCreated")
      setPage(1)
      setChosen(new Map())
    }
  }, [open, language])

  function toggle(template: AvailableTemplate) {
    if (template.status === "REJECTED") return

    setChosen((current) => {
      const next = new Map(current)
      if (next.has(template.id)) next.delete(template.id)
      else next.set(template.id, template)
      return next
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex max-h-[90dvh] w-full flex-col gap-4 overflow-hidden p-5 sm:max-w-xl">
        <DialogHeader className="gap-1 pr-8">
          <DialogTitle className="text-base">
            {t("campaigns.templates.whatsapp.campaign.add.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.templates.whatsapp.campaign.add.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 sm:flex-row">
          <SearchQuery
            placeholder={t("campaigns.channel.templatesSearchPlaceholder")}
            setQuery={(value) => {
              setQuery(value)
              setPage(1)
            }}
            defaultValue={query}
            icon={SearchIcon}
            className="w-full flex-1"
          />
          <Select
            value={selectedLanguage}
            onValueChange={(value) => {
              setSelectedLanguage(value)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-full sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("campaigns.templates.whatsapp.campaign.allLanguages")}
              </SelectItem>
              {languageOptions.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedOrder}
            onValueChange={(value) => {
              setSelectedOrder(value as keyof typeof templateSorts)
              setPage(1)
            }}
          >
            <SelectTrigger
              aria-label={t("campaigns.templates.whatsapp.campaign.sort.label")}
              className="w-full sm:w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recentlyCreated">
                {t("campaigns.templates.whatsapp.campaign.sort.recentlyCreated")}
              </SelectItem>
              <SelectItem value="recentlyUsed">
                {t("campaigns.templates.whatsapp.campaign.sort.recentlyUsed")}
              </SelectItem>
              <SelectItem value="recentlyUpdated">
                {t("campaigns.templates.whatsapp.campaign.sort.recentlyUpdated")}
              </SelectItem>
              <SelectItem value="alphabetical">
                {t("campaigns.templates.whatsapp.campaign.sort.alphabetical")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid min-h-0 grid-cols-1 gap-7 overflow-y-auto pr-1">
          {isLoadingTemplates && (
            <div className="py-8">
              <Loading />
            </div>
          )}
          {!isLoadingTemplates &&
            templates?.map((template) => {
              const isChosen = chosen.has(template.id)
              const isRejected = template.status === "REJECTED"

              return (
                <label
                  key={template.id}
                  className={`group flex min-w-0 flex-col gap-1 text-left ${
                    isRejected ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <span className="flex min-w-0 items-center justify-between gap-2 px-1 py-1">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="min-w-0 truncate text-sm font-medium transition-colors duration-150 group-hover:text-primary">
                        {template.name}
                      </span>
                      <Badge variant="secondary" className="shrink-0">
                        {languageLabel(template.language)}
                      </Badge>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {(template.status === "PENDING" || template.status === "REJECTED") && (
                        <Badge variant={template.status === "PENDING" ? "warning" : "negative"}>
                          {t(
                            template.status === "PENDING"
                              ? "campaigns.templates.whatsapp.status.PENDING"
                              : "campaigns.templates.whatsapp.status.REJECTED",
                          )}
                        </Badge>
                      )}
                      <Checkbox
                        checked={isChosen}
                        disabled={isRejected}
                        onCheckedChange={() => toggle(template)}
                      />
                    </span>
                  </span>
                  <WhatsappTemplatePreview
                    templateId={template.id}
                    body={template.body}
                    header={template.header ?? undefined}
                    footer={template.footer ?? undefined}
                    media={{ format: template.mediaFormat, url: template.mediaUrl }}
                    variables={template.whatsappTemplateVariables}
                    buttonTexts={template.whatsappTemplateButtons?.map((button) => button.text)}
                  />
                </label>
              )
            })}
          {!isLoadingTemplates && templates?.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-8 text-sm text-muted-foreground">
              {selectedLanguage !== "all" && !query
                ? t("campaigns.templates.whatsapp.campaign.noTemplatesForLanguage", {
                    language: languageLabel(selectedLanguage),
                  })
                : t("campaigns.templates.whatsapp.campaign.noAvailableTemplates")}
              <ButtonLink to="/settings/channels/whatsapp/templates/create" target="_blank">
                {t("campaigns.templates.create.title")}
                <ArrowUpRight className="size-4" />
              </ButtonLink>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3 border-t border-border pt-3">
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(count / ITEMS_PER_PAGE)}
            onPageChange={setPage}
            count={count}
          />
          <DialogFooter className="pt-0">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button
              type="button"
              disabled={chosen.size === 0}
              onClick={() => onAdd([...chosen.values()])}
            >
              {chosen.size === 0
                ? t("campaigns.templates.whatsapp.campaign.add.title")
                : t("campaigns.templates.whatsapp.campaign.addSelected", { count: chosen.size })}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
